import * as React from "react";
import { FormControl, FormGroup, FormHelperText, makeStyles, Checkbox } from "@material-ui/core";
import classnames from "classnames";

import { colors, greys, helpers } from "@styles/color.styles";
import { fontFamilySans } from "@styles/type.styles";
import { Day, DayOnOffMap } from "@ui/working-hours";

export interface DayCheckboxesProps {
  dayOnOffMap: DayOnOffMap;
  disabledDays?: DayOnOffMap; // Allows for certain days to render with a reduced opacity and no click target.
  isValid: boolean;
  invalidText?: string;
  planRestricted?: boolean;
  onToggleDay: (day: Day) => void;
}

const useStyles = makeStyles({
  formControl: {
    "& > p.MuiFormHelperText-root": {
      color: colors.red.standard,
    },
    "& .Mui-checked": {
      "&:hover": {
        background: helpers.opacity(colors.green.standard, 0.5),
        "& > * > * + *": {
          color: greys.standard,
        },
      },
    },
  },
  checkbox: {
    margin: "0px 8px 0px 0px",
    background: greys.paleGrey,
    borderRadius: 9999,
    height: 24,
    width: 24,
    "&:hover": {
      background: greys.palePaleGrey,
    },
    padding: 0,
  },
  label: {
    fontSize: 10,
    fontFamily: fontFamilySans,
    color: greys.standard,
  },
  checked: {
    background: colors.green.standard,
    "& > * > * + *": {
      color: greys.white,
    },
  },
  disabled: {
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: greys.paleGrey,
  },
  invalid: {
    background: helpers.opacity(colors.red.standard, 0.7),
    "& > * > * + *": {
      color: greys.white,
    },
    "&:hover": {
      background: helpers.opacity(colors.red.standard, 0.4),
    },
  },
  planRestricted: {
    background: colors.blueSky.lightest,
    "& > * > * + *": {
      color: colors.blueSky.darker,
    },
    pointerEvents: "none",
  },
});

export const DayCheckboxes = ({
  dayOnOffMap,
  isValid,
  onToggleDay,
  disabledDays,
  planRestricted = false,
  invalidText = "You must set working hours for at least one day.",
}: DayCheckboxesProps) => {
  const classes = useStyles();

  const renderDayCheckboxes = () => {
    return Object.keys(dayOnOffMap).map((rawDay) => {
      const day = rawDay as Day;
      const dayIcon = <span className={classes.label}>{day[0].toUpperCase()}</span>;
      return (
        <Checkbox
          key={day}
          className={classnames(classes.checkbox, {
            [classes.checked]: dayOnOffMap[day],
            [classes.invalid]: !isValid && day === Day.Monday,
            [classes.disabled]: disabledDays && disabledDays?.[day],
            [classes.planRestricted]: planRestricted,
          })}
          disabled={disabledDays && disabledDays?.[day]}
          title={day}
          checked={dayOnOffMap[day]}
          icon={dayIcon}
          checkedIcon={dayIcon}
          onChange={() => onToggleDay(day as Day)}
          name={day}
          inputProps={{ "aria-label": `day of week checkbox ${day}`, role: "checkbox" }}
        />
      );
    });
  };

  return (
    <FormControl className={classes.formControl}>
      <FormGroup row>{renderDayCheckboxes()}</FormGroup>
      {!isValid && <FormHelperText>{invalidText}</FormHelperText>}
    </FormControl>
  );
};
